import { jsPDF } from "jspdf";

// ─────────────────────────────────────────────────────────────────────────
// Calculator results → branded clinic PDF.
//
// payload = {
//   lang:    "et" | "ru" | "en"
//   title:   "HbA1c kalkulaator"
//   inputs:  [{ label, value }]                — what the patient entered
//   results: [{ label, value, hint? }]         — computed outputs
//   note?:   string                            — clinical disclaimer
// }
// ─────────────────────────────────────────────────────────────────────────

const CLINIC = {
  name: "Tallinna Endokrinoloogia Kliinik",
  addressLine1: "Tartu mnt 16, 3. krs, Tallinn",
  addressLine2: "Riia 16, 4. korrus, Pärnu",
  phone: "+372 5566 5451",
  email: "info@endokrinoloog.ee",
  url: "endokrinoloog.ee",
};

const STRINGS = {
  et: {
    documentTitle: "Tervisekalkulaatori tulemus",
    inputs: "Sisestatud andmed",
    results: "Tulemused",
    note: "Märkus",
    generated: "Genereeritud",
    disclaimer:
      "Käesolev dokument on informatiivne ega asenda arsti konsultatsiooni. " +
      "Ravimi annuste muutmine toimub alati endokrinoloogi või diabeediõe juhendamisel.",
    footer: "Broneeri vastuvõtt veebis: veebiregistratuur.ee/tallinna-endokrinoloogia-kliinik",
  },
  ru: {
    documentTitle: "Результат калькулятора здоровья",
    inputs: "Введённые данные",
    results: "Результаты",
    note: "Примечание",
    generated: "Создано",
    disclaimer:
      "Документ носит информативный характер и не заменяет консультацию врача. " +
      "Изменение доз препаратов возможно только под руководством эндокринолога или медсестры.",
    footer: "Запись онлайн: veebiregistratuur.ee/tallinna-endokrinoloogia-kliinik",
  },
  en: {
    documentTitle: "Health calculator result",
    inputs: "Inputs",
    results: "Results",
    note: "Note",
    generated: "Generated",
    disclaimer:
      "This document is informational and does not replace a physician consultation. " +
      "Medication dose changes are made only under the guidance of an endocrinologist or diabetes nurse.",
    footer: "Book online: veebiregistratuur.ee/tallinna-endokrinoloogia-kliinik",
  },
};

// Convert non-ASCII (õ, ä, ö, ü, č, š, ž, ы, Cyrillic, etc.) so the
// default Helvetica font in jsPDF does not mojibake them.
function asLatin(str) {
  if (typeof str !== "string") return String(str ?? "");
  return str
    .normalize("NFC")
    .replace(/—/g, "-")
    .replace(/–/g, "-")
    .replace(/−/g, "-")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"');
}

const SAPPHIRE = [14, 92, 140]; // #0E5C8C
const TEAL = [39, 162, 164]; // #27A2A4
const INK = [25, 41, 56];
const INK_SOFT = [82, 100, 122];

export function exportCalculatorPDF({ lang = "et", title, inputs = [], results = [], note }) {
  const t = STRINGS[lang] || STRINGS.et;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 48;

  // ── Header band ────────────────────────────────────────────────────────
  doc.setFillColor(...SAPPHIRE);
  doc.rect(0, 0, pageWidth, 90, "F");

  // Logo square
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(margin, 28, 36, 36, 6, 6, "F");
  doc.setTextColor(...SAPPHIRE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("TE", margin + 18, 52, { align: "center" });

  // Clinic name
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(asLatin(CLINIC.name), margin + 52, 46);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(asLatin(t.documentTitle), margin + 52, 64);

  // Generated date (top right)
  const stamp = new Date().toLocaleString(lang === "et" ? "et-EE" : lang === "ru" ? "ru-RU" : "en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  doc.setFontSize(9);
  doc.text(`${asLatin(t.generated)}: ${asLatin(stamp)}`, pageWidth - margin, 46, { align: "right" });

  // ── Title ──────────────────────────────────────────────────────────────
  let y = 130;
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(asLatin(title), margin, y);
  y += 8;

  // Teal underline
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(2);
  doc.line(margin, y, margin + 60, y);
  y += 28;

  // ── Inputs section ─────────────────────────────────────────────────────
  doc.setTextColor(...INK_SOFT);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(asLatin(t.inputs).toUpperCase(), margin, y);
  y += 14;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(...INK);
  inputs.forEach((row) => {
    doc.setTextColor(...INK_SOFT);
    doc.text(asLatin(row.label), margin, y);
    doc.setTextColor(...INK);
    doc.setFont("helvetica", "bold");
    doc.text(asLatin(String(row.value)), pageWidth - margin, y, { align: "right" });
    doc.setFont("helvetica", "normal");
    y += 18;
  });
  y += 10;

  // ── Results section (panel) ────────────────────────────────────────────
  const panelTop = y;
  const panelHeight = 30 + results.length * 36;
  doc.setFillColor(245, 250, 255);
  doc.setDrawColor(...SAPPHIRE);
  doc.setLineWidth(0.8);
  doc.roundedRect(margin, panelTop, pageWidth - margin * 2, panelHeight, 8, 8, "FD");

  y = panelTop + 22;
  doc.setTextColor(...SAPPHIRE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(asLatin(t.results).toUpperCase(), margin + 14, y);
  y += 18;

  results.forEach((row) => {
    doc.setTextColor(...INK_SOFT);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(asLatin(row.label), margin + 14, y);

    doc.setTextColor(...INK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text(asLatin(String(row.value)), pageWidth - margin - 14, y, { align: "right" });

    if (row.hint) {
      y += 14;
      doc.setTextColor(...INK_SOFT);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9.5);
      doc.text(asLatin(row.hint), margin + 14, y);
      doc.setFont("helvetica", "normal");
    }
    y += 22;
  });
  y = panelTop + panelHeight + 24;

  // ── Note ───────────────────────────────────────────────────────────────
  if (note) {
    doc.setTextColor(...INK_SOFT);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(asLatin(t.note).toUpperCase(), margin, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    const noteLines = doc.splitTextToSize(asLatin(note), pageWidth - margin * 2);
    doc.text(noteLines, margin, y);
    y += noteLines.length * 14 + 12;
  }

  // ── Disclaimer ─────────────────────────────────────────────────────────
  doc.setTextColor(...INK_SOFT);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  const discLines = doc.splitTextToSize(asLatin(t.disclaimer), pageWidth - margin * 2);
  doc.text(discLines, margin, y);
  y += discLines.length * 12 + 14;

  // ── Footer ─────────────────────────────────────────────────────────────
  const footerY = pageHeight - 50;
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.5);
  doc.line(margin, footerY - 10, pageWidth - margin, footerY - 10);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...INK_SOFT);
  doc.text(asLatin(CLINIC.addressLine1), margin, footerY);
  doc.text(asLatin(CLINIC.addressLine2), margin, footerY + 12);
  doc.text(asLatin(`${CLINIC.phone}  |  ${CLINIC.email}`), margin, footerY + 24);

  doc.setTextColor(...SAPPHIRE);
  doc.setFont("helvetica", "bold");
  doc.text(asLatin(t.footer), pageWidth - margin, footerY + 24, { align: "right" });

  // ── Save ───────────────────────────────────────────────────────────────
  const safeTitle = asLatin(title).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const dateStr = new Date().toISOString().slice(0, 10);
  doc.save(`endokrinoloog-${safeTitle || "kalkulaator"}-${dateStr}.pdf`);
}
